import { createSelector } from "@reduxjs/toolkit";

import type { NodeDesignsRevision, NodeAction as StateAction } from "../../schemas/index.ts";
import { nodeParentIdByChildIdSelector } from "../managed/index.ts";
import type { NautilusDesignerState } from "../managed/nautilusState.ts";
import { addNodeAction } from "../reducer/actions.ts";
import { getRedoRevision } from "../undoredo/getRedoRevision.ts";
import { getUndoRevision } from "../undoredo/getUndoRevision.ts";
import { rootNodeIdSelector } from "./designerNodeDataSelectors.ts";

export const appliedRevisionsSelector = (state: Pick<NautilusDesignerState, "appliedRevisions">) =>
  state.appliedRevisions;

export const haveRevisionInProgressSelector = (state: Pick<NautilusDesignerState, "pendingRevisionActions">) =>
  state.pendingRevisionActions.length > 0;

const UNDOABLE_ACTION_TYPES: ReadonlyArray<StateAction["type"]> = [
  "ADD",
  "MOVE",
  "REPLACE",
  "REMOVE",
  "REORDER",
  "UPDATE_CONFIG",
];

// TODO: very simplistic for now. far more rules in the original composables only version ../undoredo/getCanUndo.ts
// ideally we might want to restrict to current view or prompt and move to where the undo/redo will be
// if we do that we could include triggers as well
// to support module level we need to fix up module map state management
export const canUndoRedoSelector = createSelector(
  [haveRevisionInProgressSelector, appliedRevisionsSelector, rootNodeIdSelector, nodeParentIdByChildIdSelector],
  (revisionInProgress, appliedRevisions, rootNodeId, parentIdByChildId): { canRedo: boolean; canUndo: boolean } => {
    if (revisionInProgress) {
      return {
        canRedo: false,
        canUndo: false,
      };
    }

    const isUndoableActionType = (action: StateAction) => UNDOABLE_ACTION_TYPES.includes(action.type);

    const hasOnlyUndoableActions = ({ actions }: NodeDesignsRevision) =>
      actions.filter(isUndoableActionType).length === actions.length;

    const notAffectingRootOrImmediateChild = ({ actions }: NodeDesignsRevision) => {
      const actionNodeIds = actions
        .map((action): [StateAction, number[]] => [action, getActionNodeIds(action)])
        .filter((e) => e[1].length > 0);
      return !actionNodeIds.find(([action, ids]) => {
        if (addNodeAction.match(action)) {
          const { slotId } = action.payload;
          // for add node we need to see where it is going (not look up parent as it has none yet)
          return slotId?.parentId === rootNodeId;
        }
        return !!ids.find((id) => parentIdByChildId[id] === rootNodeId);
      });
    };

    // catches case Marc found where immediately entering the activity for a composable after replacing the activity placeholder
    // you can undo that, causing splode
    const isComposableActivitySlotAction = (action: StateAction) =>
      action.type === "REPLACE" && action.payload.replacement.typeId === "composableActivity";

    const undoRevision = getUndoRevision(appliedRevisions);
    const canUndo =
      !!undoRevision &&
      hasOnlyUndoableActions(undoRevision) &&
      notAffectingRootOrImmediateChild(undoRevision) &&
      !undoRevision.actions.find(isComposableActivitySlotAction);

    const redoRevision = getRedoRevision(appliedRevisions);
    const canRedo =
      !!redoRevision && hasOnlyUndoableActions(redoRevision) && notAffectingRootOrImmediateChild(redoRevision);
    return {
      canUndo,
      canRedo,
    };
  },
);

const getActionNodeIds = (action: StateAction) => {
  switch (action.type) {
    case "ADD":
      return [action.payload.node.id];
    case "REPLACE":
      return [action.payload.id, action.payload.replacement.id];
    case "REMOVE":
    case "REORDER":
    case "UPDATE_CONFIG":
      return [action.payload.id];
    case "MOVE":
      return [action.payload.from.parentId, action.payload.to.parentId];
    default:
      return [];
  }
};
